import { Component, Input, OnInit } from 'angular-ts-decorators';
import { SwChange } from '@core/app/scripts/classes/sw-change';
import {
  RelativeDate,
  RelativeDateLabels,
} from '@core/app/scripts/services/relative-date';
import { Moment } from 'moment';
import {
  faChevronDown,
  faChevronRight,
  faChevronUp,
} from '@fortawesome/pro-solid-svg-icons';
import { range } from 'lodash';
import { DateSource } from '@shared/app/scripts/services/date-source';

type StartEndStates = RelativeDateLabels | 'absolute';

/**
 * Component that allows picking start & end dates as absolute or relative
 * dates. To keep the input/output type the same in both modes, it works on
 * strings only.
 *
 * # Attributes
 * * start - two ways binding that accepts & sets dates in YYYY-MM-DD
 * format, or relative dates defined by {@link coreModule.relativeDate}
 * * end - two ways binding that accepts & sets dates in YYYY-MM-DD format,
 * or '-' for relative dates
 * * sw-change - callback called on changes with {value: {start, end}}
 *
 * # Example
 * <pre>
 *   <sw-start-end-input
 *     start="$ctrl.start"
 *     end="$ctrl.end"
 *     sw-change="$ctrl.onChange()">
 *   </sw-start-end-input>
 * </pre>
 */
@Component({
  selector: 'swStartEndInput',
  template: require('./sw-start-end-input.html').default,
})
export class SwStartEndInputComponent extends SwChange implements OnInit {
  @Input() start: string;
  @Input() end: string;

  startDate: Date;
  endDate: Date;

  minDate: Date;
  maxDate: Date;

  state: StartEndStates;

  relativeDates: { label: string; value: string }[] = [
    {
      value: 'today',
      label: this.$translate.instant('core.relative_dates.today'),
    },
    {
      value: 'yesterday',
      label: this.$translate.instant('core.relative_dates.yesterday'),
    },
    {
      value: 'current-week',
      label: this.$translate.instant('core.relative_dates.current_week'),
    },
    {
      value: 'last-week',
      label: this.$translate.instant('core.relative_dates.last_week'),
    },
    {
      value: 'current-month',
      label: this.$translate.instant('core.relative_dates.current_month'),
    },
    {
      value: 'last-month',
      label: this.$translate.instant('core.relative_dates.last_month'),
    },
    {
      value: 'current-quarter',
      label: this.$translate.instant('core.relative_dates.current_quarter'),
    },
    {
      value: 'last-quarter',
      label: this.$translate.instant('core.relative_dates.last_quarter'),
    },
    {
      value: 'current-year',
      label: this.$translate.instant('core.relative_dates.current_year'),
    },
    {
      value: 'last-year',
      label: this.$translate.instant('core.relative_dates.last_year'),
    },
    {
      value: 'past-365-days',
      label: this.$translate.instant('core.relative_dates.past_365_days'),
    },
  ];
  quarters: string[];
  pastYearQuarters: string[];
  months: { id: string; name: string }[];
  pastYearMonths: { id: string; name: string }[];
  weeks: string[];
  pastYearWeeks: string[];

  faChevronDown = faChevronDown;
  faChevronRight = faChevronRight;
  faChevronUp = faChevronUp;

  showDrawer: boolean = false;
  showQuarterDrawer: boolean = false;
  showPastYearQuarterDrawer: boolean;
  showWeekDrawer: boolean;
  showPastYearWeekDrawer: boolean;
  showMonthDrawer: boolean;
  showPastYearMonthDrawer: boolean;

  private DATE_FORMAT = 'YYYY-MM-DD';

  constructor(
    private relativeDate: RelativeDate,
    private moment: (any?) => Moment,
    private $translate: ng.translate.ITranslateService,
    private dateSource: DateSource,
  ) {
    'ngInject';

    super();
  }

  ngOnInit() {
    if (this.relativeDate.isLabel(this.start)) {
      this.state = <RelativeDateLabels>this.start;
    } else {
      this.state = 'absolute';
    }

    let parsed = this.relativeDate.fromLabels({
      startDate: this.start,
      endDate: this.end,
    });

    this.startDate = this.minDate = parsed.startDate;
    this.endDate = this.maxDate = parsed.endDate;

    const nowMoment = this.moment(this.dateSource.now());
    let weekCount = nowMoment.isoWeek();

    if (nowMoment.isoWeekYear() > nowMoment.year()) {
      weekCount =
        this.moment(nowMoment).subtract(1, 'month').isoWeeksInYear() + 1;
    }

    if (nowMoment.isoWeekYear() < nowMoment.year()) {
      weekCount = 1;
    }

    this.weeks = range(1, weekCount + 1).map((value) => value.toString());

    this.pastYearWeeks = range(
      1,
      this.moment(this.dateSource.now()).subtract(1, 'year').isoWeeksInYear() +
        1,
    ).map((value) => value.toString());

    this.quarters = range(1, nowMoment.quarter() + 1).map((value) =>
      value.toString(),
    );

    this.pastYearQuarters = range(1, 5).map((value) => value.toString());

    this.months = range(0, nowMoment.month() + 1).map((value) => {
      return {
        id: String(value + 1),
        name: this.moment().month(value.toString()).format('MMMM'),
      };
    });

    this.pastYearMonths = range(0, 12).map((value) => {
      return {
        id: String(value + 1),
        name: this.moment().month(value.toString()).format('MMMM'),
      };
    });
  }

  private formatDate(date: Date): string {
    return this.moment(date).format(this.DATE_FORMAT);
  }

  isActive(start: StartEndStates, end?: string) {
    if (end) {
      return this.start === start && this.end === end;
    } else {
      return this.state === start;
    }
  }

  setStart(date: Date) {
    this.startDate = this.minDate = date;
    this.start = this.formatDate(this.startDate);
    this.end = this.formatDate(this.endDate);
    this.state = 'absolute';

    this.change({
      start: this.start,
      end: this.end,
    });
  }

  setEnd(date: Date) {
    this.endDate = this.maxDate = date;
    this.start = this.formatDate(this.startDate);
    this.end = this.formatDate(this.endDate);
    this.state = 'absolute';

    this.change({ start: this.start, end: this.end });
  }

  setRelative(label: RelativeDateLabels, end?: string) {
    let relativeDates = this.relativeDate.fromLabels({
      startDate: label,
      endDate: end,
    });

    this.startDate = relativeDates.startDate;
    this.endDate = relativeDates.endDate;

    this.start = label;

    if (end) {
      this.end = end;
    } else {
      this.end = '-';
    }

    this.state = label;
    this.showDrawer = false;

    this.change({ start: this.start, end: this.end });
  }

  toggleDrawer() {
    this.showDrawer = !this.showDrawer;
  }
}
